import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Layers, Zap, ChevronDown, ChevronUp, Volume2, Play, Pause, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AudioMixerSection = ({ seriesFilter, seasonFilter }) => {
  const [expanded, setExpanded] = useState(false);
  const [mixerJobs, setMixerJobs] = useState([]);
  const [mixing, setMixing] = useState(false);
  const [sfxVolume, setSfxVolume] = useState(-14);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEpId, setPreviewEpId] = useState("");

  const pollMixerStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audio-mixer/status`);
      setMixerJobs(res.data);
      const anyRunning = res.data.some(j => j.status === "running" || j.status === "starting");
      setMixing(anyRunning);
    } catch {}
  }, []);

  useEffect(() => {
    pollMixerStatus();
    const interval = setInterval(pollMixerStatus, 5000);
    return () => clearInterval(interval);
  }, [pollMixerStatus]);

  const startBatchMix = async () => {
    try {
      await axios.post(`${API}/audio-mixer/mix-batch`, {
        series: seriesFilter,
        season: parseInt(seasonFilter),
        skip_existing: true,
        sfx_volume_db: sfxVolume,
      });
      setMixing(true);
    } catch (e) {
      console.error("Failed to start batch mix:", e);
    }
  };

  const loadPreview = async (episodeId) => {
    if (!episodeId) return;
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/audio-mixer/preview/${episodeId}`);
      setPreviewData(res.data);
    } catch (e) {
      console.error("Preview failed:", e);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="mb-6 p-4 border border-purple-500/20 rounded-lg bg-purple-900/5" data-testid="audio-mixer-section">
      <div className="flex items-center justify-between mb-1">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2" data-testid="audio-mixer-toggle">
          {expanded ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
          <Layers size={18} className="text-purple-500" />
          <h3 className="font-anton text-sm tracking-wider">AUDIO MIXER / COMPOSER</h3>
        </button>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Volume2 size={12} />
            <span>SFX Vol:</span>
            <input
              type="range"
              min={-24}
              max={0}
              value={sfxVolume}
              onChange={(e) => setSfxVolume(parseInt(e.target.value))}
              className="w-16 h-1 accent-purple-500"
              data-testid="sfx-volume-slider"
            />
            <span className="w-8 text-center">{sfxVolume}dB</span>
          </div>
          <button
            onClick={startBatchMix}
            disabled={mixing}
            className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-semibold rounded transition-colors"
            data-testid="batch-mix-btn"
          >
            {mixing ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
            Mix Season {seasonFilter}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Layer SFX sound effects onto TTS audio tracks. Parses [SFX: ...] cues from scripts and overlays matched sounds.
      </p>

      {/* Mixer Job Status */}
      {mixerJobs.map((job) => (
        <div key={job.id} className="mb-2 p-3 bg-white/5 rounded-lg" data-testid={`mixer-job-${job.id}`}>
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-semibold text-gray-300">{job.label}</span>
            <span className={`font-semibold ${
              job.status === "complete" ? "text-purple-400" :
              job.status === "error" ? "text-red-400" :
              "text-amber-400"
            }`}>
              {job.status === "complete" ? `Done — ${job.completed} mixed` :
               job.status === "error" ? "Error" :
               job.current_episode || "Processing..."}
            </span>
          </div>
          {(job.status === "running" || job.status === "starting") && (
            <div className="w-full bg-white/10 rounded-full h-1.5 mb-1">
              <div
                className="bg-purple-500 h-1.5 rounded-full transition-all"
                style={{ width: `${job.total_episodes ? (job.completed / job.total_episodes) * 100 : 0}%` }}
              />
            </div>
          )}
          <div className="flex items-center justify-between text-[10px] text-gray-500">
            <span>
              {job.completed}/{job.total_episodes} episodes
              {job.failed > 0 ? ` (${job.failed} failed)` : ""}
            </span>
            {(job.status === "running" || job.status === "starting") && (
              <button
                onClick={async () => { try { await axios.post(`${API}/audio-mixer/${job.id}/cancel`); } catch {} }}
                className="text-red-400 hover:text-red-300 underline"
                data-testid={`cancel-mix-${job.id}`}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ))}

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* SFX Placement Preview */}
          <div className="p-3 bg-white/[0.03] rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-400 font-semibold">SFX PLACEMENT PREVIEW</span>
            </div>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={previewEpId}
                onChange={(e) => setPreviewEpId(e.target.value)}
                placeholder="Paste episode ID to preview SFX timeline..."
                className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-gray-300 placeholder-gray-600"
                data-testid="preview-episode-input"
              />
              <button
                onClick={() => loadPreview(previewEpId)}
                disabled={previewLoading || !previewEpId}
                className="px-3 py-1.5 bg-purple-600/60 hover:bg-purple-600 disabled:bg-gray-700 text-white text-xs rounded transition-colors"
                data-testid="preview-sfx-btn"
              >
                {previewLoading ? "Loading..." : "Preview"}
              </button>
            </div>

            {previewData && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="font-semibold">{previewData.title}</span>
                  <span>Duration: {previewData.tts_duration_display} | {previewData.total_sfx} SFX cues | {previewData.matched_sfx} matched | {previewData.files_available} files ready</span>
                </div>

                {/* Visual timeline */}
                <div className="relative h-8 bg-white/5 rounded-lg overflow-hidden" data-testid="sfx-timeline">
                  {previewData.sfx_positions.map((pos, i) => {
                    const pct = (pos.timestamp_ms / previewData.tts_duration_ms) * 100;
                    return (
                      <div
                        key={i}
                        className={`absolute top-0 bottom-0 w-0.5 ${
                          pos.file_exists ? "bg-purple-500" : pos.matched ? "bg-yellow-500/60" : "bg-red-500/40"
                        }`}
                        style={{ left: `${pct}%` }}
                        title={`${pos.timestamp_display} — ${pos.sfx_text.slice(0, 60)}`}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-purple-500 rounded-full inline-block" /> Ready</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full inline-block" /> Matched (no file)</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full inline-block" /> Unmatched</span>
                </div>

                {/* SFX list */}
                <div className="max-h-48 overflow-y-auto space-y-1 mt-2">
                  {previewData.sfx_positions.map((pos, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] py-1 border-b border-white/5">
                      <span className="text-gray-500 w-10 flex-shrink-0 font-mono">{pos.timestamp_display}</span>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        pos.file_exists ? "bg-purple-500" : pos.matched ? "bg-yellow-500" : "bg-red-500"
                      }`} />
                      <span className="text-gray-400 truncate">{pos.sfx_text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!previewData && !previewLoading && (
              <p className="text-[10px] text-gray-600">Enter an episode ID to see where SFX would be placed in the audio timeline.</p>
            )}
          </div>

          <div className="p-3 bg-white/[0.03] rounded-lg text-xs text-gray-400 space-y-1">
            <p>Overlays [SFX: ...] cues from episode scripts onto TTS audio tracks using pydub.</p>
            <p>SFX timestamps are estimated based on word count and speaking rate.</p>
            <p>Requires TTS audio to exist first. Episodes without TTS are skipped.</p>
            <p>Output: <code className="text-purple-400">mixed_[id].mp3</code> — the final produced audio drama.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioMixerSection;
